import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { createContext, useContext, useState } from 'react'

import { message } from 'antd'

// store
import {
    uploadResume,
    uploadResumeS3Url,
} from '../modules/candidate/service/candidate.service'
import {
    getJobApplicantListByStatus,
    getStatusWiseCount,
} from '../modules/jobApplications/store/jobApplicant.slice'

const UploadResumeContext = createContext(null)

export function UploadProvider({ children }) {
    const dispatch = useDispatch()

    const [open, setOpen] = useState(false)
    const [uploadingList, setUploadingList] = useState([])

    const location = useLocation()
    const jobId = location.pathname.split('/')[3]

    const handleAiUploadUrl = async (files, status, listStatus) => {
        try {
            const payload = {
                resumes: files.map((f) => ({
                    resumeUrl: f.publicUrl,
                    fileType: f.type,
                    fileName: f.name,
                })),
                jobId,
                status,
            }

            const response = await fetch(
                window.location.hostname === 'localhost'
                    ? process.env.REACT_APP_URL + '/backend/candidate'
                    : `https://pmx.softwareco.com/backend/candidate`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    credentials: 'include',
                }
            )

            const reader = response.body?.getReader()
            if (!reader) throw new Error('No readable stream from server')

            const decoder = new TextDecoder('utf-8')

            while (true) {
                const { done, value } = await reader.read()
                if (done) break

                const chunk = decoder.decode(value, { stream: true })

                chunk.split('\n').forEach((line) => {
                    if (!line.startsWith('data:')) return
                    try {
                        const json = JSON.parse(line.replace('data: ', ''))

                        if (json?.error) {
                            message.error(json.error)
                            setUploadingList((prev) =>
                                prev.map((f) =>
                                    files.some((ff) => ff.uid === f.uid)
                                        ? {
                                              ...f,
                                              status: 'error',
                                              step: json.error,
                                          }
                                        : f
                                )
                            )
                            return
                        }

                        if (json?.message) {
                            setUploadingList((prev) =>
                                prev.map((f) =>
                                    files.some((ff) => ff.uid === f.uid)
                                        ? {
                                              ...f,
                                              step: json.message,
                                              status: json.message.includes(
                                                  'successfully'
                                              )
                                                  ? 'done'
                                                  : 'in_progress',
                                          }
                                        : f
                                )
                            )
                            if (json?.message.includes('successfully')) {
                                dispatch(
                                    getJobApplicantListByStatus({
                                        filter: {
                                            job: jobId,
                                            status: listStatus,
                                        },
                                    })
                                )
                                dispatch(getStatusWiseCount({ jobId: jobId }))
                            }
                        }
                    } catch (err) {
                        console.warn('Invalid JSON in stream:', line)
                    }
                })
            }
        } catch (error) {
            console.error('Upload failed:', error)
            setUploadingList((prev) =>
                prev.map((f) =>
                    files.some((ff) => ff.uid === f.uid)
                        ? {
                              ...f,
                              status: 'error',
                              step: 'Upload failed. Please try again.',
                          }
                        : f
                )
            )
            message.error('Upload failed. Please try again.')
        }
    }

    const addUploads = async (files, status, listStatus) => {
        const uploadedFiles = []

        for (const fileWrap of files) {
            const file = fileWrap.originFileObj || fileWrap
            const uid = file.uid || Date.now().toString()

            // Add to UI
            const newItem = {
                uid,
                name: file.name,
                type: file.type,
                status: 'in_progress',
                progress: 0,
                step: 'Uploading to S3...',
            }
            setUploadingList((prev) => [...prev, newItem])
            setOpen(true)

            try {
                const { data } = await uploadResume({
                    data: [{ filename: file.name, contentType: file.type }],
                })
                const { uploadUrl, publicLink } = data.data[0]

                await uploadResumeS3Url(file, uploadUrl)

                uploadedFiles.push({
                    uid,
                    name: file.name,
                    type: file.type,
                    publicUrl: publicLink,
                })

                setUploadingList((prev) =>
                    prev.map((f) =>
                        f.uid === uid
                            ? { ...f, step: 'Uploaded. Waiting for parsing...' }
                            : f
                    )
                )
            } catch (err) {
                console.error('Upload failed:', err)
                setUploadingList((prev) =>
                    prev.map((f) =>
                        f.uid === uid
                            ? { ...f, status: 'error', step: 'Upload failed' }
                            : f
                    )
                )
            }
        }

        if (uploadedFiles.length > 0) {
            await handleAiUploadUrl(uploadedFiles, status, listStatus)
        }
    }

    const updateUpload = (uid, updates) => {
        setUploadingList((prev) =>
            prev.map((f) => (f.uid === uid ? { ...f, ...updates } : f))
        )
    }

    const removeUpload = (uid) => {
        setUploadingList((prev) => prev.filter((f) => f.uid !== uid))
    }

    return (
        <UploadResumeContext.Provider
            value={{
                open,
                setOpen,
                uploadingList,
                addUploads,
                updateUpload,
                removeUpload,
            }}
        >
            {children}
        </UploadResumeContext.Provider>
    )
}

export const useUpload = () => useContext(UploadResumeContext)
